/**
 * TanStack Query hooks for Branch resource.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@client/lib/api-client';

export interface Branch {
  id: string;
  code: string;
  name: string;
  address: string;
  phone?: string | null;
  email?: string | null;
  status: 'active' | 'inactive' | 'closed';
  openingDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListBranchesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
}

export const branchKeys = {
  all: ['branches'] as const,
  list: (params?: ListBranchesParams) => ['branches', 'list', params] as const,
  detail: (id: string) => ['branches', 'detail', id] as const,
};

export function useBranches(params?: ListBranchesParams) {
  return useQuery({
    queryKey: branchKeys.list(params),
    queryFn: () =>
      api.getPage<Branch>('/api/branches', params as Record<string, string>),
  });
}

export function useBranch(id: string) {
  return useQuery({
    queryKey: branchKeys.detail(id),
    queryFn: () => api.get<Branch>(`/api/branches/${id}`),
    enabled: !!id,
  });
}

export function useCreateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<Branch>('/api/branches', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: branchKeys.all });
    },
  });
}

export function useUpdateBranch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.patch<Branch>(`/api/branches/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: branchKeys.all });
    },
    onError: (err) => err, // caller handles ApiError
  });
}

/** Type guard for ApiError */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}
